import { Controller } from "@hotwired/stimulus"
import { get, patch } from "@rails/request.js"
import { clearButtonBusy, setButtonLoading } from "../lib/button_loading"

// Public-URL handle card: explicit Verify → Save flow.
//
//   * Save starts disabled. Verify/Save are only actionable once the handle is
//     changed (dirty). They disable once that change is saved.
//   * Editing the handle clears any prior verification and re-disables Save.
//   * Verify checks availability; a unique handle shows a ✓ and enables Save,
//     anything else shows the reason and keeps Save disabled.
//   * Save persists via AJAX, toasts, and resets the card (Save disabled, the
//     just-saved value becomes the new baseline) so no page reload is needed.
//   * Publishing is a separate, explicit action (see shared/_surface_publication
//     .html.haml's Publish/Unpublish button) — saving a handle never publishes
//     it. That button is gated here too: a dirty input disables it (finish the
//     URL first), and a server-locked button (no saved handle yet) is never
//     re-enabled client-side (see the publishBtn gating in _sync()).
//
// Feedback is rendered with textContent + className toggling (never innerHTML)
// since the reason strings, while server-controlled, share a DOM context with
// the user-entered handle.

// A11Y-1 / BUTTON-4 (#13355 / #13356): every control this gate disables says
// why, as its `data-gl-tooltip` plus an accessible name of "<label> — <reason>"
// (the card-dirty shape), for exactly as long as it is disabled. The partials
// paint the same pair on a first frame that renders disabled
// (gl_disabled_reason_attrs; shared/_card_form_actions stamps saveUnchanged
// as data-slug-verify-disabled-reason) — keep the copy in step.
export const DISABLED_REASONS = {
  verifyEmpty: "Enter a handle first",
  saveUnchanged: "Change the handle first",
  saveUnverified: "Verify the handle first",
  viewUnverified: "Verify and save your handle first",
  viewUnpublished: "Publish first",
  publishUnsaved: "Save a public URL first",
}
const NAME_SEPARATOR = " — "

const REASON_MESSAGES = {
  too_short: "Too short — at least 3 characters",
  too_long: "Too long — 63 characters max",
  invalid_format: "Lowercase letters, numbers, and hyphens only",
  reserved: "That handle is reserved",
  taken: "That handle is already taken",
}

export default class extends Controller {
  static targets = ["input", "verifyButton", "saveButton", "feedback", "cancelButton", "viewButton"]
  // savedMessage/removedMessage let the same flow drive other unique-handle cards
  // (e.g. the Email Services "Public Email" card) with the right toast copy;
  // they default to the Public URL wording for existing markup.
  static values = {
    availabilityUrl: String,
    savedMessage: { type: String, default: "Public URL saved" },
    removedMessage: { type: String, default: "Public URL removed" },
    // Site-slug surfaces support "unpublish" (clearing the handle), so an emptied
    // field is a valid Save. Surfaces that always require a handle (e.g. the email
    // handle) set this false so Save stays disabled until the field is re-verified.
    allowBlankSave: { type: Boolean, default: true },
    // Opt-in: id of a turbo-frame to reload after a successful save, for surfaces
    // where saving changes server-rendered state OUTSIDE this form (the Calendar
    // publication section — saving a handle now publishes the calendar). The frame
    // is a sibling of the form, so it's referenced by id rather than as a target.
    refreshFrameId: { type: String, default: "" },
  }

  connect() {
    this.baseline = this._currentValue()
    // A saved (non-blank) handle is verified by construction: it passed the
    // uniqueness Verify check when it was saved and remains the agent's own
    // unique slug, so it can't silently become unavailable. Surface that on load
    // (and on every return visit) instead of resetting to unverified. Editing
    // clears it via onInput; re-verifying re-confirms; saving keeps it.
    // NOTE: verified does NOT imply published — see _publishedState().
    this.verified = this.baseline !== ""
    if (this.verified) this._renderVerified()
    this._armPublicationRefresh()
    this._sync()
  }

  // input -> slug-verify#onInput
  onInput() {
    this.verified = false
    this._clearFeedback()
    this._sync()
  }

  // click -> slug-verify#verify
  async verify() {
    const slug = this._currentValue()
    if (!this._isActionable() || !slug) return

    // BUTTON-8: busy keeps the label (shared spinner + aria-busy).
    setButtonLoading(this.verifyButtonTarget, true)
    try {
      const response = await get(this.availabilityUrlValue, {
        query: { slug },
        responseKind: "json",
      })
      const data = response.ok ? await response.json : null
      // Discard a stale response: the user edited the handle while this request
      // was in flight, so onInput already reset `verified` for the new value and
      // marking the old slug verified would enable Save for an unverified handle.
      if (this._currentValue() !== slug) return
      if (!data) { this._renderError("Unavailable"); return }

      if (data.available) {
        this.verified = true
        this._renderVerified()
      } else {
        this.verified = false
        this._renderUnavailable(data.reason)
      }
    } catch {
      if (this._currentValue() !== slug) return
      this.verified = false
      this._renderError("Unavailable")
    } finally {
      clearButtonBusy(this.verifyButtonTarget)
      this._sync()
    }
  }

  // submit -> slug-verify#save
  async save(event) {
    event.preventDefault()
    const slug = this._currentValue()
    const unpublishing = slug === ""
    // Save requires a changed (dirty) handle, verified unless it's a blank unpublish.
    if (!this._isActionable() || (!unpublishing && !this.verified)) return

    setButtonLoading(this.saveButtonTarget, true)
    try {
      await this._persist(slug)
    } finally {
      clearButtonBusy(this.saveButtonTarget)
      this._sync()
    }
  }

  disconnect() {
    this._disarmPublicationRefresh()
  }

  // The publication section inside the refresh frame re-renders server-side
  // without touching this form: the Publish/Unpublish toggle replaces the frame
  // via turbo_stream, and _refreshFrame() reloads it after a save. Each of
  // those renders can flip the publish state the view buttons are gated on, so
  // watch both render paths and re-_sync() against the fresh DOM.
  _armPublicationRefresh() {
    if (!this.refreshFrameIdValue) return
    this._publicationRefresh = (event) => {
      // Frame navigation/reload: turbo:frame-render fires AFTER the frame
      // rendered, so the marker is already fresh — sync directly.
      if (event.type === "turbo:frame-render") {
        if (event.target.id === this.refreshFrameIdValue) this._sync()
        return
      }
      // turbo:before-stream-render fires BEFORE the stream applies. Wrap the
      // render step (the documented Turbo hook for post-render work) so the
      // re-sync reads the replaced frame, not the stale one.
      if (event.target.getAttribute("target") !== this.refreshFrameIdValue) return
      const render = event.detail.render
      event.detail.render = async (streamElement) => {
        await render(streamElement)
        this._sync()
      }
    }
    document.addEventListener("turbo:frame-render", this._publicationRefresh)
    document.addEventListener("turbo:before-stream-render", this._publicationRefresh)
  }

  _disarmPublicationRefresh() {
    if (!this._publicationRefresh) return
    document.removeEventListener("turbo:frame-render", this._publicationRefresh)
    document.removeEventListener("turbo:before-stream-render", this._publicationRefresh)
    this._publicationRefresh = null
  }

  // Called by Save: PATCHes the handle, adopts the server-normalized slug as
  // the new baseline on success, and reports whether the save succeeded.
  // Callers re-_sync() afterwards.
  async _persist(slug) {
    const unpublishing = slug === ""
    try {
      const response = await patch(this.element.getAttribute("action"), {
        body: { public_slug: slug },
        responseKind: "json",
      })
      const data = await response.json
      if (response.ok && data.ok) {
        // Server returns the normalized slug, or null when the handle was cleared.
        const savedSlug = data.slug || ""
        this._toast(unpublishing ? this.removedMessageValue : this.savedMessageValue)
        // Adopt the server-normalized slug as the new baseline (and show it), so
        // re-typing the normalized form isn't seen as dirty (no no-op re-save).
        this.inputTarget.value = savedSlug
        this.baseline = savedSlug
        // A saved handle is verified by construction; a cleared handle is not (an
        // empty field can't re-verify), so reflect the new publish state here.
        // Not-dirty + verified means _sync() disables both Verify and Save;
        // editing the handle later clears it via onInput.
        this.verified = savedSlug !== ""
        if (this.verified) this._renderVerified()
        else this._clearFeedback()
        // Keep slug-based view buttons pointing at the newly-saved slug (their
        // hrefs were rendered with the old slug). Buttons marked static-href
        // (e.g. the token-based View Widget URL) don't embed the slug at all.
        if (savedSlug) {
          this.viewButtonTargets.forEach((btn) => {
            if (btn.dataset.staticHref === "true") return
            if (btn.dataset.urlTemplate) {
              btn.href = btn.dataset.urlTemplate.replace("__SLUG__", savedSlug)
            } else {
              btn.href = btn.href.replace(/\/[^/]+$/, `/${savedSlug}`)
            }
          })
        }
        this._refreshFrame()
        return true
      }
      // Force a re-verify before the next save: the handle may have been taken
      // between Verify and Save, so a stale `verified` must not keep Save live.
      this.verified = false
      this._renderError((data.errors && data.errors[0]) || "Could not save")
      return false
    } catch {
      this.verified = false
      this._renderError("Could not save")
      return false
    }
  }

  // Reload the opted-in sibling turbo-frame so server-rendered state that a
  // save just changed (e.g. the Calendar publication section) re-renders. An
  // inline frame has no src yet, so the first refresh points it at the current
  // page; Turbo extracts just the matching frame from the response.
  _refreshFrame() {
    if (!this.refreshFrameIdValue) return
    const frame = document.getElementById(this.refreshFrameIdValue)
    if (!frame) return
    if (frame.src) frame.reload()
    else frame.src = window.location.href
  }

  // Live publish state, read from the marker the publication partial renders
  // inside the refresh frame (data-slug-verify-published). Reading the DOM each
  // _sync — rather than caching a value at connect — keeps the gate in step
  // with every server re-render of the frame. Three-valued: true/false on
  // surfaces with a publication section, null where there is none (e.g. the
  // email-handle card, which has no publication concept) so verification alone
  // keeps gating there.
  _publishedState() {
    if (!this.refreshFrameIdValue) return null
    const frame = document.getElementById(this.refreshFrameIdValue)
    const marker = frame && frame.querySelector("[data-slug-verify-published]")
    return marker ? marker.getAttribute("data-slug-verify-published") === "true" : null
  }

  // The Publish/Unpublish button (shared/_surface_publication.html.haml) lives
  // in the sibling refresh frame, outside this form entirely. Queried fresh
  // each _sync — never cached at connect — so a server re-render of the frame
  // (Publish/Unpublish turbo_stream, post-save reload) is picked up.
  _publishButton() {
    if (!this.refreshFrameIdValue) return null
    const frame = document.getElementById(this.refreshFrameIdValue)
    return frame ? frame.querySelector("[data-surface-publish-button]") : null
  }

  _currentValue() {
    return this.inputTarget.value.trim()
  }

  _isDirty() {
    return this._currentValue() !== this.baseline
  }

  _isActionable() {
    return this._isDirty()
  }

  // Verify is disabled only while the field is empty (nothing to verify);
  // any non-empty state keeps it clickable and color-codes it instead — green
  // "Verified" once the handle checks out, red-outline while changed (see
  // _renderVerifyButton). Save only follows a successful verify of the
  // current actionable (dirty) handle.
  _sync() {
    const dirty = this._isDirty()
    const actionable = this._isActionable()
    const empty = this._currentValue() === ""
    // A blank field only counts as a valid (unpublish) Save on surfaces that opt in.
    const canSaveEmpty = empty && this.allowBlankSaveValue
    // There's nothing to verify in an empty field, so Verify is disabled until a
    // handle is typed. This also re-enables the button after a verify attempt
    // (verify() disables it in-flight and finishes here). Non-empty states are
    // color-coded instead of disabled (see _renderVerifyButton).
    if (this.hasVerifyButtonTarget) {
      this.verifyButtonTarget.disabled = empty
      // The button's label (Verify / Verified) is settled before its reason is
      // composed from it.
      this._renderVerifyButton()
      this._syncDisabledReason(this.verifyButtonTarget, empty, DISABLED_REASONS.verifyEmpty)
    }
    // Save requires a changed (dirty) handle, plus verification (or an allowed
    // blank unpublish).
    const saveGated = !(actionable && (this.verified || canSaveEmpty))
    this.saveButtonTarget.disabled = saveGated
    this._syncDisabledReason(this.saveButtonTarget, saveGated, this._saveReason(actionable, empty))
    // Cancel is only meaningful once the handle has been edited.
    if (this.hasCancelButtonTarget) this.cancelButtonTarget.classList.toggle("d-none", !dirty)
    // An edited handle is unsaved work even while Save waits on Verify: the
    // CRM setup tour reads this before it leaves the page (#16204).
    this.element.dataset.slugVerifyDirty = String(dirty)
    // Slug-based view buttons (View Website / View Landing Page / Copy Page
    // URL) are only valid destinations once the handle is verified AND — on
    // surfaces with a publication section — the record is actually published.
    // Verified alone is NOT enough there: a manual Unpublish keeps the handle,
    // so the landing page 404s while the slug still looks verified. Buttons
    // marked static-href (e.g. the token-based View/Copy Widget URL) don't
    // embed the slug, so verification never gates them — but the published
    // gate still applies: the token URL 404s unpublished too (issue #9686).
    const published = this._publishedState()
    this.viewButtonTargets.forEach((btn) => {
      const slugGated = btn.dataset.staticHref !== "true" && !this.verified
      const gated = published === false || slugGated
      btn.classList.toggle("disabled", gated)
      btn.setAttribute("aria-disabled", String(gated))
      this._syncDisabledReason(btn, gated, slugGated ? DISABLED_REASONS.viewUnverified : DISABLED_REASONS.viewUnpublished)
      // .gl-btn.disabled's pointer-events: none only blocks mouse hit-testing;
      // a focused anchor still activates on Enter. Drop gated links out of the
      // tab order so keyboard users can't follow a dead href / fire the copy.
      if (gated) btn.setAttribute("tabindex", "-1")
      else btn.removeAttribute("tabindex")
    })
    // Owner's UX law: gate by enablement. The Publish button acts on the SAVED
    // handle, so a dirty input disables it (finish the URL first). A button the
    // server locked (no saved handle) is never re-enabled from the client.
    // The reason rides the same tooltip + name pair as every other gate here
    // (BUTTON-4: never a native title, #13448); the partial paints it on a
    // first frame that renders locked and hands us the copy as
    // data-disabled-reason.
    const publishBtn = this._publishButton()
    if (publishBtn) {
      const gated = publishBtn.dataset.publishLocked === "true" || dirty
      publishBtn.disabled = gated
      this._syncDisabledReason(publishBtn, gated, publishBtn.dataset.disabledReason || DISABLED_REASONS.publishUnsaved)
    }
  }

  // Why Save is disabled right now: an unchanged handle (the partial's painted
  // default, overridable through data-slug-verify-disabled-reason), an emptied
  // one on a surface that cannot save blank, or a changed handle not yet verified.
  _saveReason(actionable, empty) {
    if (!actionable) return this.saveButtonTarget.dataset.slugVerifyDisabledReason || DISABLED_REASONS.saveUnchanged
    return empty ? DISABLED_REASONS.verifyEmpty : DISABLED_REASONS.saveUnverified
  }

  // A11Y-1: a disabled control explains why — tooltip plus "<label> — <reason>"
  // name while gated, both withdrawn the moment the control enables (an
  // enabled Save must not keep telling the user to change the handle first).
  // Mirrors card_dirty_controller#_syncDisabledReason.
  _syncDisabledReason(el, gated, reason) {
    if (gated) {
      this._rememberAuthoredName(el, reason)
      const label = this._authoredNames.get(el) || this._labelOf(el)
      el.setAttribute("data-gl-tooltip", reason)
      el.setAttribute("aria-label", label ? `${label}${NAME_SEPARATOR}${reason}` : reason)
    } else {
      el.removeAttribute("data-gl-tooltip")
      this._restoreAuthoredName(el)
    }
  }

  // The name the markup gave the control before this controller touched it. A
  // server-painted composition ("<label> — <reason>", from
  // gl_disabled_reason_attrs) is recognised as ours, never doubled, and removed
  // like any other applied reason.
  _rememberAuthoredName(el, reason) {
    this._authoredNames ||= new WeakMap()
    if (this._authoredNames.has(el)) return
    const existing = el.getAttribute("aria-label")
    const painted = existing && existing.endsWith(`${NAME_SEPARATOR}${reason}`)
    this._authoredNames.set(el, painted ? null : existing)
  }

  _restoreAuthoredName(el) {
    if (!this._authoredNames?.has(el)) return
    const authored = this._authoredNames.get(el)
    if (authored === null) el.removeAttribute("aria-label")
    else el.setAttribute("aria-label", authored)
    this._authoredNames.delete(el)
  }

  _labelOf(el) {
    return (el.textContent || "").replace(/\s+/g, " ").trim()
  }

  // The Verify button doubles as a status badge: green check-circle +
  // "Verified" when the handle is verified, red-outline check-circle + "Verify"
  // while verification is pending (changed). The neutral untouched state keeps
  // the default shield.
  _renderVerifyButton() {
    if (!this.hasVerifyButtonTarget) return

    const pending = this._isActionable() && !this.verified
    const icon = this._icon(this.verified || pending ? "ph-check-circle" : "ph-shield-check")
    icon.classList.add("me-1")
    this.verifyButtonTarget.replaceChildren(icon, document.createTextNode(this.verified ? "Verified" : "Verify"))
    this.verifyButtonTarget.classList.toggle("gl-btn-verified", this.verified)
    this.verifyButtonTarget.classList.toggle("gl-btn-verify-pending", pending)
  }

  _renderVerified() {
    // The green Verify button (see _renderVerifyButton) conveys the verified
    // state now, so no separate green message is shown — just clear any prior error.
    this._clearFeedback()
  }

  _renderUnavailable(reason) {
    this._setFeedback("gl-slug-availability mt-1 text-danger text-end", "ph-x-circle", REASON_MESSAGES[reason] || "Unavailable")
  }

  _renderError(message) {
    this._setFeedback("gl-slug-availability mt-1 text-danger text-end", "ph-x-circle", message)
  }

  _clearFeedback() {
    if (!this.hasFeedbackTarget) return
    this.feedbackTarget.className = "gl-slug-availability mt-1 text-end"
    this.feedbackTarget.replaceChildren()
  }

  _setFeedback(className, iconName, message) {
    if (!this.hasFeedbackTarget) return
    this.feedbackTarget.className = className
    this.feedbackTarget.replaceChildren(this._icon(iconName), document.createTextNode(` ${message}`))
  }

  _icon(name) {
    const el = document.createElement("i")
    el.className = `ph ${name}`
    return el
  }

  // Route through the one canonical toast (CSS consolidation Tier 2b):
  // window.showToast builds the gl-toast card into #toast-container and the
  // gl-toast Stimulus controller drives its lifecycle.
  _toast(message, kind = "success") {
    window.showToast?.(message, kind === "success" ? "bg-success" : "bg-danger")
  }
}
